/* eslint-disable react/jsx-handler-names */
import { useEffect, useRef } from 'react';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl';
import classNames from 'classnames';

import { getRedemptionStorage } from '@jsmdg/browser-storage';
import { Accordion, CheckboxRadioAlign, RadioButton } from '@jsmdg/yoshi';
import { type UseBuyboxReturn } from '../../hooks/useBuybox';
import { useProduct } from '../../hooks/useProduct';
import { useRedemptionCart } from '../../hooks/useRedemptionCart';
import { PurchaseType } from '../../types/enums/purchaseType';
import { Divider } from '../Divider/Divider';
import { FriendsDiscountBadge } from '../FriendsDiscountBadge';
import { FriendsDiscountSelect } from '../FriendsDiscountSelect';
import { RedeemInfo } from '../RedeemInfo';
import { RedemptionInfoBanner } from '../RedemptionInfoBanner/RedemptionInfoBanner';
import {
    trackBuyTicketClicked,
    trackBuyVoucherClicked,
    trackTicketInfoClicked,
    trackVoucherInfoClicked,
} from './voucherAndTicketSelector.tracking';
import styles from './VoucherAndTicketSelector.module.scss';

const messages = defineMessages({
    appointmentSelectorText: {
        defaultMessage: 'Wähle im nächsten Schritt Ort und Termin aus',
    },
    singleLocationMessage: {
        defaultMessage: 'Wähle im nächsten Schritt einen Termin aus',
    },
    a11yRadioGutscheinKaufenButton: {
        defaultMessage: 'Gutschein kaufen',
    },
    a11yRadioTerminBuchenButton: {
        defaultMessage: 'Termin sofort buchen',
    },
});

type VoucherAndTicketSelectorProps = {
    readonly minParticipants: number;
    readonly buyboxProps: UseBuyboxReturn;
    readonly maxParticipants: number;
    readonly numberOfLocations: number;
    readonly setDiscountSelectIsOpen: (isOpen: boolean) => void;
    readonly isDiscountSelectOpen: boolean;
};

const VoucherAndTicketSelector = ({
    buyboxProps,
    isDiscountSelectOpen,
    maxParticipants,
    minParticipants,
    numberOfLocations,
    setDiscountSelectIsOpen,
}: VoucherAndTicketSelectorProps): JSX.Element => {
    const { isRedemptionCartFilled } = useRedemptionCart();
    const {
        bookableLocationsCount,
        handlePurchaseType,
        handleSeatCountChange,
        hasFriendsDiscount,
        purchaseType,
        seatCount,
    } = buyboxProps;
    const intl = useIntl();
    const { productGroup, productNudge } = useProduct();

    const toggleVoucher = (): void => {
        handlePurchaseType(PurchaseType.Voucher);
    };

    const toggleTicket = (): void => {
        handlePurchaseType(PurchaseType.Ticket);
    };

    const handleVoucherInfoClick = (): void => {
        toggleVoucher();
        trackBuyVoucherClicked();
    };

    const handleVoucherInfoIconClick = (): void => {
        toggleVoucher();
        trackVoucherInfoClicked();
    };

    const handleTicketInfoClick = (): void => {
        toggleTicket();
        trackBuyTicketClicked();
    };

    const handleTicketInfoIconClick = (): void => {
        toggleTicket();
        trackTicketInfoClicked();
    };

    const redemptionStorage = getRedemptionStorage();

    const linkRef = useRef<HTMLAnchorElement | HTMLButtonElement>(null);

    useEffect(() => {
        const removeRedemptionStorage = () => {
            redemptionStorage.clear();
            window.location.reload();
        };

        // The Accordion item is disabled,
        // to handle click on link inside a disabled accordion item,
        // we need to use EventListener otherwise the onclick event will be ignored.
        const link = linkRef.current;
        link?.addEventListener('click', removeRedemptionStorage);

        return () => {
            link?.removeEventListener('click', removeRedemptionStorage);
        };
    }, [redemptionStorage]);

    const accordionItemIds = ['purchase-type-voucher', 'purchase-type-ticket'];

    return (
        <div className="pt-2x px-3x px-xs-6x">
            {(!productNudge || Boolean(productGroup.length)) && <Divider className="mb-2x" />}
            {hasFriendsDiscount && <FriendsDiscountBadge />}
            <Accordion
                activeItemsIds={
                    purchaseType === PurchaseType.Ticket
                        ? [accordionItemIds[1]]
                        : [accordionItemIds[0]]
                }
            >
                <Accordion.Item
                    isDisabled={isRedemptionCartFilled}
                    id={accordionItemIds[0]}
                    onClick={handleVoucherInfoClick}
                    onIconClick={handleVoucherInfoIconClick}
                    subTitle={
                        <div
                            data-testid="voucherLocationCount"
                            className={`theme-text-body-text ${styles.radioText}`}
                        >
                            {numberOfLocations > 0 && (
                                <div className={styles.linkContainer}>
                                    <RedeemInfo />
                                </div>
                            )}
                        </div>
                    }
                    leftComponent={
                        <RadioButton
                            onChange={handleVoucherInfoClick}
                            id="purchase-type-voucher"
                            dataTestid="purchase-type-voucher"
                            value="voucher"
                            className="pt-1-5x"
                            checked={purchaseType === PurchaseType.Voucher}
                            textAlign={CheckboxRadioAlign.Top}
                            disabled={isRedemptionCartFilled}
                            aria-label={intl.formatMessage(messages.a11yRadioGutscheinKaufenButton)}
                            aria-labelledby="purchase-type-voucher"
                        />
                    }
                    title={
                        <>
                            <div className="position-relative d-flex align-items-center flex-grow-1">
                                <div
                                    className={classNames(
                                        styles.toggleLayerVoucherSelection,
                                        'h-50',
                                    )}
                                />

                                <div
                                    className={classNames('d-flex flex-column flex-grow-1', {
                                        [styles.disabled]: isRedemptionCartFilled,
                                    })}
                                >
                                    <strong className="theme-text-subtitle-text pr-2x">
                                        <FormattedMessage defaultMessage="Gutschein kaufen" />
                                    </strong>
                                </div>
                            </div>
                            {isRedemptionCartFilled && <RedemptionInfoBanner />}
                        </>
                    }
                    className="pb-2x pt-0-5x"
                >
                    <FriendsDiscountSelect
                        selectedSeatCount={seatCount}
                        handleSeatCountChange={handleSeatCountChange}
                        minParticipants={minParticipants || 1}
                        maxParticipants={maxParticipants || 1}
                        setDiscountSelectIsOpen={setDiscountSelectIsOpen}
                    />
                </Accordion.Item>
                <Accordion.Item
                    id={accordionItemIds[1]}
                    onClick={handleTicketInfoClick}
                    onIconClick={handleTicketInfoIconClick}
                    isDisabled={isDiscountSelectOpen}
                    leftComponent={
                        <RadioButton
                            id="purchase-type-ticket"
                            dataTestid="purchase-type-ticket"
                            value="ticket"
                            checked={purchaseType === PurchaseType.Ticket}
                            textAlign={CheckboxRadioAlign.Top}
                            className="pt-1-5x"
                            onChange={handleTicketInfoClick}
                            aria-label={intl.formatMessage(messages.a11yRadioTerminBuchenButton)}
                        />
                    }
                    subTitle={
                        <div className={classNames('theme-text-body-text', styles.radioText)}>
                            <FormattedMessage
                                defaultMessage="Aktuell an {bookableLocationsCount} {bookableLocationsCount, plural, one {Ort} other {Orten}} verfügbar"
                                values={{
                                    bookableLocationsCount,
                                }}
                            />
                        </div>
                    }
                    title={
                        <div className="position-relative d-flex align-items-center flex-grow-1">
                            <div className={classNames(styles.toggleLayer, 'h-100')} />
                            <div className="d-flex flex-column flex-grow-1">
                                <strong className="theme-text-subtitle-text pr-2x">
                                    <FormattedMessage defaultMessage="Termin sofort buchen" />
                                </strong>
                            </div>
                        </div>
                    }
                    className={classNames(styles.accordionItem, 'pb-2x pt-0-5x')}
                    contentClasses={styles.accordionItem}
                >
                    <div
                        className={classNames(
                            'theme-text-caption-text py-2x pl-4x',
                            styles.infoText,
                        )}
                    >
                        {intl.formatMessage(
                            bookableLocationsCount > 1
                                ? messages.appointmentSelectorText
                                : messages.singleLocationMessage,
                        )}
                    </div>
                </Accordion.Item>
            </Accordion>
        </div>
    );
};

export { VoucherAndTicketSelector };
